import React from 'react';
// mui
import { Box, Typography, useTheme } from '@mui/material';
//components
import CallLog from 'src/components/CallLog';
// interfaces
import CSS from 'csstype';
import { CallLog as CallLogInterface } from 'src/hooks/core-api/useCallHistory';
// icons
import { IconContext } from 'react-icons';
// utils
import { fPhone } from 'src/utils/formatPhone';
import { parseDate } from 'src/utils/formatTime';
import { BiLinkAlt, BiMessageCheck } from 'react-icons/bi';
import { IoMdHelp } from 'react-icons/io';
import { RiCustomerService2Fill } from 'react-icons/ri';
//
import { dayKey, generateDayLabel } from '../shared/pagination-labels';

interface CallHistoryListProps {
  callHistoryGroup: CallLogInterface[];
  previousCallHistoryGroup: CallLogInterface[]; // Pagination creates multiple page
}

const toSortTime = (value: string | null) => parseDate(value)?.getTime() ?? 0;

const sortCallHistory = (callHistoryGroup: CallLogInterface[]) =>
  [...callHistoryGroup].sort((a, b) => toSortTime(b.end_time) - toSortTime(a.end_time));

const getDayKeyFromArrayEnd = (
  callHistoryGroup: CallLogInterface[],
  direction: 'start' | 'end'
): string | null => {
  // Takes first or last element of call history group and returns its day bucket
  if (callHistoryGroup.length === 0) {
    return null;
  }

  const queryIndex = direction === 'start' ? 0 : callHistoryGroup.length - 1;
  return dayKey(parseDate(callHistoryGroup[queryIndex].end_time));
};

interface CallLogMetadataProps {
  icon: React.ReactNode;
  label: string;
  iconVerticalAlign?: CSS.Properties['verticalAlign'];
  children?: React.ReactNode;
}

const CallHistoryList: React.FC<CallHistoryListProps> = ({
  callHistoryGroup,
  previousCallHistoryGroup,
}) => {
  const theme = useTheme();
  // Because of pagination, we need to sort the previous and current call
  // history groups and see if a new day label is needed
  //
  // (previous groups may have items that are on the same day of items in the
  // current group)
  const sortedCallHistory = sortCallHistory(callHistoryGroup);
  const sortedPreviousCallHistory = sortCallHistory(previousCallHistoryGroup);
  let lastDayKey = getDayKeyFromArrayEnd(sortedPreviousCallHistory, 'end'); // Not using setState() because we don't want to re-render

  const contrastText = theme.palette.text.secondary;
  const success = theme.palette.success.main;
  const info = theme.palette.info.main;
  const violet = '#7141ff';

  const CallLogMetadata: React.FC<CallLogMetadataProps> = ({ icon, label, iconVerticalAlign }) => (
    <Box sx={{ display: 'flex' }}>
      <IconContext.Provider value={{ style: { verticalAlign: iconVerticalAlign } }}>
        <div>{icon}</div>
      </IconContext.Provider>
      <Typography variant="body2" color={contrastText} sx={{ display: 'inline-block', ml: 0.75 }}>
        {label}
      </Typography>
    </Box>
  );

  return (
    <>
      {sortedCallHistory.map((callLog, i) => {
        const currentLogDate = parseDate(callLog.end_time);
        const currentDayKey = dayKey(currentLogDate);
        const needsNewDayLabel = currentDayKey !== lastDayKey;

        lastDayKey = currentDayKey;

        return (
          <React.Fragment key={i}>
            {needsNewDayLabel && (
              <Typography variant="h6" sx={{ my: 2 }}>
                {generateDayLabel(currentLogDate)}
              </Typography>
            )}
            <Box sx={{ py: 2, px: 1 }} key={i}>
              <CallLog
                log={{
                  from: fPhone(callLog.from_formatted),
                  label: callLog.location_name,
                  direction: callLog.direction,
                  callEndTime: callLog.end_time,
                  duration: callLog.duration,
                }}
              >
                <Box sx={{ mt: 1 }}>
                  {callLog.total_questions_answered > 0 && (
                    <CallLogMetadata
                      icon={<IoMdHelp size={20} color={violet} />}
                      iconVerticalAlign="text-bottom"
                      label={`${callLog.total_questions_answered} question${
                        callLog.total_questions_answered > 1 ? 's' : ''
                      } answered `}
                    />
                  )}
                  {callLog.total_messages_sent > 0 && (
                    <CallLogMetadata
                      icon={<BiMessageCheck size={20} color={success} />}
                      iconVerticalAlign="middle"
                      label={`Sent booking message`}
                    />
                  )}
                  {callLog.total_shortlink_visits > 0 && (
                    <CallLogMetadata
                      icon={
                        <BiLinkAlt size={20} color={info} style={{ verticalAlign: 'middle' }} />
                      }
                      iconVerticalAlign="middle"
                      label={`${callLog.total_shortlink_visits} link click${
                        callLog.total_shortlink_visits > 1 ? 's' : ''
                      }`}
                    />
                  )}
                  {callLog.connected_to_shop && (
                    <CallLogMetadata
                      icon={
                        <RiCustomerService2Fill size={20} style={{ verticalAlign: 'middle' }} />
                      }
                      iconVerticalAlign="middle"
                      label={`Connected to shop`}
                    />
                  )}
                </Box>
              </CallLog>
            </Box>
          </React.Fragment>
        );
      })}
    </>
  );
};

export default CallHistoryList;
